import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import type { MarketMarketIO } from "../../generated/fresh-guard-api";
import { queryKeys } from "../query-keys";

export function useMarketUpdateOptimistic() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: { marketMarketIO: MarketMarketIO }) => {
      console.log(input.marketMarketIO);

      const res = await api.marketApi.apiMarketPost(input.marketMarketIO);
      return res.data;
    },
    onMutate: async ({ marketMarketIO }) => {
      await qc.cancelQueries({ queryKey: queryKeys.markets.all });

      const previousList = qc.getQueryData<MarketMarketIO[]>(
        queryKeys.markets.all,
      );
      const tempId = -Date.now();

      const optimistic: MarketMarketIO = {
        ...marketMarketIO,
        id: tempId,
      };

      qc.setQueryData<MarketMarketIO[]>(queryKeys.markets.all, (old) => {
        const list = old ?? [];
        return [optimistic, ...list];
      });

      return { previousList, tempId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        qc.setQueryData(queryKeys.markets.all, context.previousList);
      }
    },
    onSuccess: (created, _vars, context) => {
      qc.setQueryData<MarketMarketIO[]>(queryKeys.markets.all, (old) => {
        const list = old ?? [];

        if (!context) {
          return [created, ...list.filter((m) => m.id !== created.id)];
        }

        const replaced = list.map((m) =>
          m.id === context.tempId ? created : m,
        );
        const hasCreated = replaced.some((m) => m.id === created.id);

        return hasCreated ? replaced : [created, ...replaced];
      });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.markets.all });
    },
    meta: {
      skipGlobalError: false,
    },
  });
}
